import { useState, useEffect, useRef } from 'react';
import { critiqueResume, refineCritiqueSuggestion, ResumeCritiqueSuggestion, ResumeScorecard } from '../../services/resume';
import './ResumeCritiqueModal.css';

type Phase = 'loading' | 'results' | 'error';

interface ResumeCritiqueModalProps {
  onClose: () => void;
  onApplySuggestion: (suggestion: ResumeCritiqueSuggestion) => Promise<void>;
}

const SECTION_LABELS: Record<string, string> = {
  summary: 'Professional Summary',
  experience: 'Experience',
  skills: 'Skills',
  ats_readiness: 'ATS Readiness',
};

const STATUS_ICON: Record<string, string> = {
  excellent: '✓',
  good: '✓',
  needs_work: '!',
  missing: '✗',
};

function scoreTier(score: number): 'excellent' | 'good' | 'needs_work' | 'missing' {
  if (score >= 90) return 'excellent';
  if (score >= 70) return 'good';
  if (score >= 40) return 'needs_work';
  return 'missing';
}

function ScorecardPanel({ scorecard }: { scorecard: ResumeScorecard }) {
  const tier = scoreTier(scorecard.overall);

  return (
    <div className="rcm-scorecard" data-testid="critique-scorecard">
      <div className="rcm-scorecard-header">
        <span className="rcm-scorecard-title">Resume Score</span>
        <span className={`rcm-overall-score rcm-score--${tier}`}>
          {scorecard.overall}<span className="rcm-score-denom">/100</span>
        </span>
      </div>

      <div className="rcm-scorecard-sections">
        {(Object.entries(scorecard.sections) as [string, typeof scorecard.sections.summary][]).map(([key, section]) => (
          <div key={key} className="rcm-section-row">
            <div className="rcm-section-row-top">
              <span className="rcm-section-row-name">{SECTION_LABELS[key] ?? key}</span>
              <div className="rcm-section-row-right">
                <span className={`rcm-section-status rcm-section-status--${section.status}`}>
                  {STATUS_ICON[section.status]} {section.status.replace('_', ' ')}
                </span>
                <span className={`rcm-section-score rcm-score--${scoreTier(section.score)}`}>
                  {section.score}
                </span>
              </div>
            </div>
            <p className="rcm-section-comment">{section.comment}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ResumeCritiqueModal({ onClose, onApplySuggestion }: ResumeCritiqueModalProps) {
  const [phase, setPhase] = useState<Phase>('loading');
  const [elapsed, setElapsed] = useState(0);
  const [analysisDone, setAnalysisDone] = useState(false);
  const [suggestions, setSuggestions] = useState<ResumeCritiqueSuggestion[]>([]);
  const [scorecard, setScorecard] = useState<ResumeScorecard | null>(null);
  const [applyingId, setApplyingId] = useState<string | null>(null);
  const [appliedIds, setAppliedIds] = useState<Set<string>>(new Set());
  const [errorMsg, setErrorMsg] = useState('');
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Per-card edit & refine state
  const [proposedTexts, setProposedTexts] = useState<Record<string, string>>({});
  const [editingId, setEditingId] = useState<string | null>(null);
  const [refineOpenId, setRefineOpenId] = useState<string | null>(null);
  const [refineFeedbacks, setRefineFeedbacks] = useState<Record<string, string>>({});
  const [refiningId, setRefiningId] = useState<string | null>(null);
  const [refineErrors, setRefineErrors] = useState<Record<string, string>>({});

  const getProposedText = (s: ResumeCritiqueSuggestion) => proposedTexts[s.id] ?? s.proposedChange;

  const steps = [
    { label: 'Scanning your resume',       isDone: () => elapsed >= 2 },
    { label: 'Retrieving career memory',   isDone: () => elapsed >= 5 },
    { label: 'Applying ATS guidelines',    isDone: () => elapsed >= 9 },
    { label: 'Generating recommendations', isDone: () => analysisDone },
  ];

  const stepDone   = (i: number) => steps[i].isDone();
  const stepActive = (i: number) => !stepDone(i) && (i === 0 || stepDone(i - 1));

  useEffect(() => {
    timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
    let cancelled = false;

    (async () => {
      try {
        const result = await critiqueResume(8);
        if (cancelled) return;
        if (timerRef.current) clearInterval(timerRef.current);
        setAnalysisDone(true);

        if (result.scorecard) setScorecard(result.scorecard);
        const loaded = result.suggestions || [];
        setSuggestions(loaded);

        // Initialise editable texts from LLM output
        const texts: Record<string, string> = {};
        loaded.forEach((s: ResumeCritiqueSuggestion) => { texts[s.id] = s.proposedChange; });
        setProposedTexts(texts);

        setPhase('results');
      } catch (err: any) {
        if (cancelled) return;
        if (timerRef.current) clearInterval(timerRef.current);
        setErrorMsg(err?.message || 'Failed to generate critique. Please try again.');
        setPhase('error');
      }
    })();

    return () => {
      cancelled = true;
      if (timerRef.current) clearInterval(timerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApply = async (suggestion: ResumeCritiqueSuggestion) => {
    const effectiveSuggestion = { ...suggestion, proposedChange: getProposedText(suggestion) };
    setApplyingId(suggestion.id);
    try {
      await onApplySuggestion(effectiveSuggestion);
      setAppliedIds((prev) => new Set([...prev, suggestion.id]));
    } finally {
      setApplyingId(null);
    }
  };

  const handleDismiss = (id: string) => {
    setSuggestions((prev) => prev.filter((s) => s.id !== id));
  };

  const toggleEdit = (id: string) => {
    setEditingId((prev) => (prev === id ? null : id));
    setRefineOpenId(null); // close refine when editing
  };

  const toggleRefine = (id: string) => {
    setRefineOpenId((prev) => (prev === id ? null : id));
    setEditingId(null); // close edit when refining
    setRefineErrors((prev) => ({ ...prev, [id]: '' }));
  };

  const handleRefine = async (s: ResumeCritiqueSuggestion) => {
    const feedback = refineFeedbacks[s.id]?.trim();
    if (!feedback) return;

    setRefiningId(s.id);
    setRefineErrors((prev) => ({ ...prev, [s.id]: '' }));

    try {
      const newText = await refineCritiqueSuggestion({
        description: s.description,
        sectionAffected: s.sectionAffected,
        currentProposedChange: getProposedText(s),
        userFeedback: feedback,
      });
      setProposedTexts((prev) => ({ ...prev, [s.id]: newText }));
      setRefineFeedbacks((prev) => ({ ...prev, [s.id]: '' }));
      setRefineOpenId(null);
    } catch (err: any) {
      setRefineErrors((prev) => ({ ...prev, [s.id]: err?.message || 'Failed to refine. Try again.' }));
    } finally {
      setRefiningId(null);
    }
  };

  const priorityConfig = {
    high:   { label: 'High impact',   cls: 'rcm-priority--high' },
    medium: { label: 'Medium impact', cls: 'rcm-priority--medium' },
    low:    { label: 'Low impact',    cls: 'rcm-priority--low' },
  };

  const visible = suggestions.filter((s) => !appliedIds.has(s.id));
  const totalApplied = appliedIds.size;

  const subtitleText = () => {
    if (phase === 'loading') return 'Analyzing your resume against ATS best practices…';
    if (phase === 'error') return 'Analysis failed';
    if (visible.length === 0 && totalApplied === 0) return 'Your resume looks strong!';
    if (visible.length === 0) return `All ${totalApplied} suggestion${totalApplied !== 1 ? 's' : ''} applied!`;
    return `${visible.length} suggestion${visible.length !== 1 ? 's' : ''} found${totalApplied > 0 ? ` · ${totalApplied} applied` : ''}`;
  };

  return (
    <div className="rcm-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="rcm-modal" role="dialog" aria-modal="true" aria-label="AI Resume Critique" data-testid="critique-modal">

        {/* Header */}
        <div className="rcm-header">
          <div>
            <h3 className="rcm-title">AI Resume Critique</h3>
            <p className="rcm-subtitle">{subtitleText()}</p>
          </div>
          <button type="button" className="rcm-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        {/* Body */}
        <div className="rcm-body">

          {/* Loading */}
          {phase === 'loading' && (
            <div className="rcm-loading">
              <div className="rcm-steps">
                {steps.map(({ label }, i) => (
                  <div
                    key={label}
                    className={`rcm-step ${stepDone(i) ? 'rcm-step--done' : stepActive(i) ? 'rcm-step--active' : 'rcm-step--pending'}`}
                  >
                    <span className="rcm-step-icon">
                      {stepDone(i) ? '✓' : stepActive(i) ? '⟳' : '○'}
                    </span>
                    <span className="rcm-step-label">{label}</span>
                  </div>
                ))}
              </div>
              <p className="rcm-elapsed">{elapsed}s</p>
            </div>
          )}

          {/* Error */}
          {phase === 'error' && (
            <div className="rcm-empty">
              <div className="rcm-empty-icon">⚠️</div>
              <p className="rcm-empty-text">{errorMsg}</p>
              <button type="button" className="rcm-btn rcm-btn--secondary" onClick={onClose}>Close</button>
            </div>
          )}

          {/* Results */}
          {phase === 'results' && (
            <div className="rcm-results-layout">

              {/* Scorecard — always shown */}
              {scorecard && <ScorecardPanel scorecard={scorecard} />}

              {/* Suggestions */}
              {visible.length > 0 && (
                <div className="rcm-suggestions" data-testid="critique-suggestions">
                  <p className="rcm-suggestions-label">Recommended improvements</p>
                  {visible.map((s) => {
                    const pKey = (s.priority || 'medium') as keyof typeof priorityConfig;
                    const pCfg = priorityConfig[pKey] ?? priorityConfig.medium;
                    const isApplying = applyingId === s.id;
                    const isRefining = refiningId === s.id;
                    const isBusy = applyingId !== null || refiningId !== null;
                    const isEditing = editingId === s.id;
                    const refineOpen = refineOpenId === s.id;
                    const currentText = getProposedText(s);

                    return (
                      <div key={s.id} className="rcm-card" data-testid={`critique-suggestion-${s.id}`}>
                        <div className="rcm-card-top">
                          <span className={`rcm-priority ${pCfg.cls}`}>{pCfg.label}</span>
                          <span className="rcm-section-tag">{s.sectionAffected}</span>
                        </div>

                        <p className="rcm-card-title">{s.description}</p>

                        {s.impact && <p className="rcm-card-impact">→ {s.impact}</p>}

                        <p className="rcm-card-reason">{s.reason}</p>

                        {/* Proposed change — editable */}
                        <div className="rcm-proposed">
                          <div className="rcm-proposed-header">
                            <span className="rcm-proposed-label">Proposed change</span>
                            <button
                              type="button"
                              className="rcm-edit-toggle"
                              onClick={() => toggleEdit(s.id)}
                              disabled={isBusy}
                            >
                              {isEditing ? 'Done' : 'Edit'}
                            </button>
                          </div>
                          {isEditing ? (
                            <textarea
                              className="rcm-proposed-textarea"
                              value={currentText}
                              onChange={(e) => setProposedTexts((prev) => ({ ...prev, [s.id]: e.target.value }))}
                              rows={4}
                              disabled={isBusy}
                            />
                          ) : (
                            <p className="rcm-proposed-text">{currentText}</p>
                          )}
                        </div>

                        {/* Refine panel */}
                        {refineOpen && (
                          <div className="rcm-refine-panel">
                            <span className="rcm-refine-label">What should be different?</span>
                            <textarea
                              className="rcm-refine-input"
                              placeholder="e.g. I don't have exact numbers, keep it qualitative… or make it more senior-sounding…"
                              value={refineFeedbacks[s.id] || ''}
                              onChange={(e) => setRefineFeedbacks((prev) => ({ ...prev, [s.id]: e.target.value }))}
                              rows={2}
                              disabled={isRefining}
                              autoFocus
                            />
                            {refineErrors[s.id] && (
                              <p className="rcm-refine-error">{refineErrors[s.id]}</p>
                            )}
                            <div className="rcm-refine-actions">
                              <button
                                type="button"
                                className="rcm-btn rcm-btn--primary"
                                onClick={() => handleRefine(s)}
                                disabled={isRefining || !refineFeedbacks[s.id]?.trim()}
                              >
                                {isRefining ? 'Generating…' : '↻ Generate'}
                              </button>
                            </div>
                          </div>
                        )}

                        <div className="rcm-card-actions">
                          <button
                            type="button"
                            className="rcm-btn rcm-btn--primary"
                            onClick={() => handleApply(s)}
                            disabled={isBusy}
                            data-testid={`resume-apply-critique-${s.id}`}
                          >
                            {isApplying ? 'Applying…' : 'Apply'}
                          </button>
                          <button
                            type="button"
                            className={`rcm-btn ${refineOpen ? 'rcm-btn--secondary' : 'rcm-btn--ghost'}`}
                            onClick={() => toggleRefine(s.id)}
                            disabled={isBusy}
                          >
                            {refineOpen ? 'Cancel' : 'Refine'}
                          </button>
                          <button
                            type="button"
                            className="rcm-btn rcm-btn--ghost"
                            onClick={() => handleDismiss(s.id)}
                            disabled={isBusy}
                          >
                            Dismiss
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {visible.length === 0 && (
                <div className="rcm-empty rcm-empty--inline">
                  <p className="rcm-empty-text">
                    {totalApplied > 0
                      ? `All ${totalApplied} suggestion${totalApplied !== 1 ? 's' : ''} applied — great work!`
                      : 'No further improvements needed. Your resume is well-optimized!'}
                  </p>
                  <button type="button" className="rcm-btn rcm-btn--primary" onClick={onClose}>Done</button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
